import React from "react";
import {
  TextInput,
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useRedirect,
  useRefresh,
  CreateProps,
  FormDataConsumer,
} from "react-admin";
import { GetParam } from "utils/tools";
import { Grid, Typography } from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import CustomCreateToolbar from "components/CustomCreateToolbar";
import CustomMonthPickerInput from "components/CustomMonthPickerInput";
import SubcontractorHolidayPaymentRouteTypeInput from "./components/SubcontractorHolidayPaymentRouteTypeInput";
import AcademicYearInput from "components/AcademicYearInput";
import CcaAcademicYearInput from "components/CcaAcademicYearInput";
import AcademicTermInput from "components/AcademicTermInput";
import CcaAcademicTermInput from "components/CcaAcademicTermInput";
import SubcontractorHolidayPaymentPricingTypeInput from "./components/SubcontractorHolidayPaymentPricingTypeInput";
import CreateWrapper from "../../components/CreateWrapper";

interface ICustomRedirect {
  data: ISubcontractorHolidayPayments;
}

const getInputBasedRouteType = (type: string, schoolId: number) => {
  switch (type) {
    case "academicYears":
      return (
        <>
          <Grid item xs={4}>
            <AcademicYearInput schoolId={schoolId} />
          </Grid>
          <Grid item xs={8}></Grid>

          <Grid item xs={4}>
            <AcademicTermInput isRequired />
          </Grid>
          <Grid item xs={8}></Grid>
        </>
      );
    case "ccaAcademicYears":
      return (
        <>
          <Grid item xs={4}>
            <CcaAcademicYearInput schoolId={schoolId} />
          </Grid>
          <Grid item xs={8}></Grid>

          <Grid item xs={4}>
            <CcaAcademicTermInput isRequired />
          </Grid>
          <Grid item xs={8}></Grid>
        </>
      );
  }
};

const SubcontractorHolidayPaymentsCreate = (props: CreateProps) => {
  const param = GetParam();
  const redirect = useRedirect();
  const refresh = useRefresh();

  const breadcrumb = [
    { title: "Schools", link: `/schools` },
    {
      title: "SubcontractorHolidayPayments",
      link: `/schools/${param.get("schoolId")}/show/6`,
    },
    { title: "Create", link: "" },
  ];

  const CustomRedirect = ({ data }: ICustomRedirect) => {
    redirect(`/schools/${data.schoolId}/show/6`);
    refresh();
  };

  const transform = async (data: any) => {
    data.holidayPeriodMonth = data.month;
    data.holidayPeriodYear = data.year;

    data.routeType = data.routeType === "academicYears" ? "Regular" : "CCA";

    if (data.routeType === "Regular") {
      data.regularRouteJoinPeriodYearId = data.academicYearId;
      data.regularRouteJoinPeriodTermId = data.academicTermId;
    }

    if (data.routeType === "CCA") {
      data.ccaRouteJoinPeriodYearId = data.ccaAcademicYearId;
      data.ccaRouteJoinPeriodTermId = data.ccaAcademicTermId;
    }

    delete data.holidayPeriod;
    delete data.year;
    delete data.month;
    delete data.routeJoinPeriodTermId;
    delete data.academicYearId;
    delete data.academicTermId;
    delete data.ccaAcademicYearId;
    delete data.ccaAcademicTermId;

    return data;
  };

  return (
    <>
      <CreateWrapper
        breadcrumb={breadcrumb}
        headerTitle="New Subcontractor Holiday Payment"
        transform={transform}
        onSuccess={CustomRedirect}
        {...props}
      >
        <SimpleForm toolbar={<CustomCreateToolbar />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School</Typography>
            </Grid>
            <Grid item xs={4}>
              <ReferenceInput
                label="Select School"
                source="schoolId"
                reference="schools"
                disabled
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Details</Typography>
            </Grid>

            <Grid item xs={4}>
              <TextInput source="name" label="Name" validate={required()} />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <SubcontractorHolidayPaymentRouteTypeInput />
            </Grid>
            <Grid item xs={8}></Grid>

            <FormDataConsumer>
              {({ formData }) =>
                getInputBasedRouteType(formData.routeType, formData.schoolId)
              }
            </FormDataConsumer>

            <Grid item xs={4}>
              <CustomMonthPickerInput
                name="holidayPeriod"
                label="Holiday Period"
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <SubcontractorHolidayPaymentPricingTypeInput />
            </Grid>
            <Grid item xs={8}></Grid>
          </SanitizedGrid>
        </SimpleForm>
      </CreateWrapper>
    </>
  );
};

export default SubcontractorHolidayPaymentsCreate;
